/**
 * Pure helpers for the "Everyone at {organization}" group row of the share
 * model (SE-8359, PV17 — R06 on SE-8246, SE-8355 contract): the role
 * vocabulary across the two wire enums, the presence-row rule, the
 * effective-role caption and the cache writes on the `listGroupGrants` read
 * both entity adapters share.
 *
 * Typed structurally over the generated shapes so the helpers stay usable
 * from either feature without importing an entity's own query type.
 * @module features/scouting/utils/shareGroupGrants
 */

import {
  GroupGrantRole,
  UserWatchlistGrantSource,
  UserWatchlistRole,
  type ScoutingGroupGrantFragment,
  type ScoutingListGroupGrantsQuery,
} from "@/generated/graphql";

import type { ShareGrantRole } from "./shareWatchlistGrants";

/** One group grant as the `listGroupGrants` read returns it. */
export type ShareGroupGrantItem = ScoutingGroupGrantFragment;

/** The cached read every group write updates. */
type GroupGrantsRead = ScoutingListGroupGrantsQuery;

/** A group the owner may invite (from `eligibleShareGroups`). */
export interface ShareGroupCandidate {
  /** `ShareGroupKey` value; `EVERYONE` in v1. */
  readonly key: string;
  /** "Everyone at {organization}". */
  readonly label: string;
  /** ACTIVE members the grant reaches (scenario: ACTIVE MANAGER+). */
  readonly memberCount: number;
}

/** One WHO HAS ACCESS group line. */
export interface ShareGroupAccessRowItem extends ShareGroupCandidate {
  /** The grant id — the role-change and revoke target. */
  readonly groupGrantId: string;
  /** EDITOR or VIEWER, in the share vocabulary. Never OWNER. */
  readonly role: ShareGrantRole;
  /** Whether a role change or revoke for this line is in flight. */
  readonly isPending: boolean;
}

/**
 * Group wire role → the share vocabulary the rows render.
 * @param role - `GroupGrant.role`
 * @returns The matching share role
 */
export const toShareRoleFromGroup = (role: GroupGrantRole): ShareGrantRole =>
  role === GroupGrantRole.Editor
    ? UserWatchlistRole.Editor
    : UserWatchlistRole.Viewer;

/**
 * Share vocabulary → the input enum the group mutations accept.
 * @param role - The grantable role picked in the dialog
 * @returns The `GroupGrantRole` input value
 */
export const toGroupWireRole = (role: ShareGrantRole): GroupGrantRole =>
  role === UserWatchlistRole.Editor
    ? GroupGrantRole.Editor
    : GroupGrantRole.Viewer;

/**
 * Whether a grant row is a GROUP presence row — the group's audience, not a
 * grant (SE-8355). A read that predates the field is DIRECT.
 * @param row - Any grant row carrying the optional `grantSource`
 * @param row.grantSource - DIRECT, GROUP, or absent on an older read
 * @returns True for a presence row
 */
export const isPresenceRow = (row: {
  readonly grantSource?: UserWatchlistGrantSource | null;
}): boolean => row.grantSource === UserWatchlistGrantSource.Group;

/** Rank of a role for the max(direct, group) comparison. */
const ROLE_WEIGHT: Readonly<Record<UserWatchlistRole, number>> = {
  [UserWatchlistRole.Viewer]: 1,
  [UserWatchlistRole.Editor]: 2,
  [UserWatchlistRole.Owner]: 3,
};

/**
 * Caption under a person whose direct role is outranked by the group's, so
 * the owner understands why lowering it changes nothing (R06 §3).
 * @param input - The person's direct role and the group, if any
 * @param input.role - The person's DIRECT role
 * @param input.groupRole - The group grant's role, or null without a group
 * @param input.groupLabel - The group's label, or null without a group
 * @returns "Also editor via Everyone at …", or null
 */
export const effectiveRoleCaption = ({
  role,
  groupRole,
  groupLabel,
}: {
  readonly role: UserWatchlistRole;
  readonly groupRole: ShareGrantRole | null;
  readonly groupLabel: string | null;
}): string | null => {
  if (groupRole == null || groupLabel == null) return null;
  if (role === UserWatchlistRole.Owner) return null;
  if (ROLE_WEIGHT[groupRole] <= ROLE_WEIGHT[role]) return null;
  const noun = groupRole === UserWatchlistRole.Editor ? "editor" : "viewer";
  return `Also ${noun} via ${groupLabel}`;
};

/**
 * Whether the invite search should offer the group: pinned first on an empty
 * query, and kept while the query matches its label.
 * @param label - The group's label
 * @param query - Raw search text
 * @returns True when the candidate is offered
 */
export const groupCandidateMatches = (
  label: string,
  query: string
): boolean => {
  const needle = query.trim().toLowerCase();
  return needle === "" || label.toLowerCase().includes(needle);
};

/**
 * Appends a freshly created group grant to the cached read.
 * @param previous - Cached query result, or undefined when nothing is cached
 * @param grant - The grant returned by `createGroupGrant`
 * @returns The next cache value, or undefined to leave the cache untouched.
 */
export const appendGroupGrant = (
  previous: GroupGrantsRead | null | undefined,
  grant: ShareGroupGrantItem
): GroupGrantsRead | undefined => {
  if (!previous) return undefined;
  if (previous.listGroupGrants.some(existing => existing.id === grant.id)) {
    return previous;
  }
  return { ...previous, listGroupGrants: [...previous.listGroupGrants, grant] };
};

/**
 * Writes a returned grant over its cached row (a repeat `createGroupGrant`
 * or an `updateGroupGrantRole` answer), appending when the id is new.
 * @param previous - Cached query result, or undefined when nothing is cached
 * @param grant - The grant the mutation returned
 * @returns The next cache value, or undefined to leave the cache untouched.
 */
export const replaceGroupGrant = (
  previous: GroupGrantsRead | null | undefined,
  grant: ShareGroupGrantItem
): GroupGrantsRead | undefined => {
  if (!previous) return undefined;
  if (!previous.listGroupGrants.some(existing => existing.id === grant.id)) {
    return appendGroupGrant(previous, grant);
  }
  return {
    ...previous,
    listGroupGrants: previous.listGroupGrants.map(existing =>
      existing.id === grant.id ? grant : existing
    ),
  };
};

/**
 * Removes a revoked group grant from the cached read.
 * @param previous - Cached query result, or undefined when nothing is cached
 * @param groupGrantId - The revoked grant id
 * @returns The next cache value, or undefined to leave the cache untouched.
 */
export const removeGroupGrant = (
  previous: GroupGrantsRead | null | undefined,
  groupGrantId: string
): GroupGrantsRead | undefined => {
  if (!previous) return undefined;
  const grants = previous.listGroupGrants.filter(
    grant => grant.id !== groupGrantId
  );
  if (grants.length === previous.listGroupGrants.length) return previous;
  return { ...previous, listGroupGrants: grants };
};
